<script setup lang="ts">
import { ExclamationCircleIcon } from "@heroicons/vue/20/solid";
import { ChevronDownIcon, ChevronLeftIcon } from "@heroicons/vue/24/outline";
import { Accordion as RadixAccordion } from "radix-vue/namespaced";
import { twMerge } from "tailwind-merge";
import { computed, ref } from "vue";

import { useUser } from "@/composables/api/useUser";

import GiftIcon from "@/components/icons/GiftIcon.vue";
import PiggyBankIcon from "@/components/icons/PiggyBankIcon.vue";
import TicketIcon from "@/components/icons/TicketIcon.vue";
import Accordion from "@/components/ui/Accordion.vue";

const { data: user } = useUser();

type Item =
  | { text: string; icon: any; image?: never }
  | { text: string; image: string; icon?: never };

const items: Item[] = [
  {
    icon: TicketIcon,
    text: "howItWorks.items.1",
  },
  {
    icon: PiggyBankIcon,
    text: "howItWorks.items.2",
  },
];

const itemsGalbani: Item[] = [
  ...items,
  {
    image: "/images/small-circular-kitchen-box-icon.png",
    text: "howItWorks.items.3",
  },
];

const itemsLactel: Item[] = [
  ...items,
  {
    icon: GiftIcon,
    text: "howItWorks.items.3",
  },
];

const currentItems = computed<Item[]>(() =>
  user.value?.current_mechanic === "lactel" ? itemsLactel : itemsGalbani,
);

const isProductsListExpanded = ref(false);

// function openMailto() {
//   window.open(
//     "mailto:contact@topchef.galbani.fr?subject=Programme%20de%20r%C3%A9compenses%20Galbani%20x%20Top%20Chef",
//     "_blank",
//   );
// }
</script>

<template>
  <main class="mx-auto flex max-w-6xl flex-col gap-8 px-6 pt-5 pb-14 lg:pt-10 lg:pb-24">
    <button
      type="button"
      class="text-secondary flex cursor-pointer items-center gap-2 text-base font-medium underline lg:text-lg"
      aria-label="Retourner à la page précédente"
      @click="
        user?.current_mechanic === 'lactel'
          ? $router.push({ name: 'vouchers' })
          : $router.push({ name: 'gifts' })
      "
    >
      <ChevronLeftIcon class="size-4" aria-hidden="true" />
      {{ $t("navigation.back") }}
    </button>

    <div class="mx-auto flex max-w-3xl flex-col gap-8 lg:gap-12">
      <section
        id="how-it-works"
        class="flex flex-col gap-5 lg:gap-6"
        aria-labelledby="how-it-works-title"
      >
        <h1
          id="how-it-works-title"
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold lg:text-[32px]',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("howItWorks.title") }}
        </h1>

        <p class="text-secondary text-base font-medium lg:text-lg">
          {{ $t("howItWorks.subtitle") }}
        </p>

        <ul class="flex flex-col gap-4" role="list">
          <li
            v-for="item in currentItems"
            :key="item.text"
            :class="
              twMerge(
                'bg-galbani/10 text-galbani flex items-center gap-3 rounded-[20px] p-3 text-base lg:gap-4 [&>img]:size-8 [&>img]:shrink-0 [&>img]:rounded-full [&>p>span]:font-bold [&>svg]:size-8 [&>svg]:shrink-0',
                user?.current_mechanic === 'lactel' && 'bg-lactel-blue-light text-lactel-blue',
              )
            "
          >
            <Component :is="item.icon" v-if="item.icon" aria-hidden="true" />

            <img v-if="item.image" :src="item.image" alt="" aria-hidden="true" />

            <p v-html="$t(item.text)" />
          </li>
        </ul>

        <button
          v-if="user?.current_mechanic === 'galbani'"
          type="button"
          :class="
            twMerge(
              'text-secondary mx-auto flex cursor-pointer items-center gap-2 text-sm underline lg:text-lg',
            )
          "
          aria-label="Consulter le règlement complet du programme"
          @click="$router.push({ query: { modal: 'Terms' } })"
        >
          <ExclamationCircleIcon class="size-5 rotate-180 lg:size-6" aria-hidden="true" />
          {{ $t("howItWorks.terms") }}
        </button>

        <a
          v-if="user?.current_mechanic === 'lactel'"
          target="_blank"
          rel="noopener noreferrer"
          :class="
            twMerge(
              'text-lactel-grey mx-auto flex cursor-pointer items-center gap-2 text-sm underline lg:text-lg',
            )
          "
          aria-label="Consulter le règlement complet du programme"
          :href="$t('footer.terms.url')"
        >
          <ExclamationCircleIcon class="size-5 rotate-180 lg:size-6" aria-hidden="true" />
          {{ $t("howItWorks.terms") }}
        </a>
      </section>

      <hr class="hidden h-px w-full border-0 bg-black/50 lg:block" aria-hidden="true" />

      <section
        id="eligible-products"
        class="flex flex-col gap-5 lg:gap-6"
        aria-labelledby="products-title"
      >
        <h2
          id="products-title"
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold lg:text-[32px]',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("howItWorks.products.title") }}
        </h2>

        <ul
          v-if="user?.current_mechanic === 'galbani'"
          class="text-secondary list-disc pl-5 text-base font-medium lg:text-lg"
          role="list"
          v-html="$t('howItWorks.products.list')"
        />

        <div
          v-else
          class="flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-8 sm:justify-start"
        >
          <img
            src="/lactel/images/lactel-matin-leger.png"
            alt="Lactel matin leger"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel-bio.png"
            alt="Lactel matin léger bio"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel-matin-leger-bio.png"
            alt="Lactel bio"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel_vitamine_d.png"
            alt="Lactel"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel-lait-chevre.png"
            alt="Lactel lait de chèvre"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel-lait-brebis.png"
            alt="Lactel lait de brebis"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel-vitavie.png"
            alt="Lactel vitavie"
            class="h-[75px] lg:h-[105px]"
          />

          <img
            src="/lactel/images/lactel_caffe_latte.png"
            alt="Lactel Caffè Latte"
            class="h-[75px] lg:h-[105px]"
          />
        </div>

        <Transition name="slide">
          <ul
            v-if="isProductsListExpanded"
            class="text-secondary list-disc pl-5 text-base font-medium lg:text-lg"
            role="list"
            v-html="$t('howItWorks.products.listExpanded')"
          />
        </Transition>

        <button
          v-if="user?.current_mechanic === 'galbani'"
          type="button"
          class="text-secondary mx-auto flex cursor-pointer items-center gap-2 text-base font-medium underline lg:text-lg"
          @click="isProductsListExpanded = !isProductsListExpanded"
        >
          {{
            !isProductsListExpanded
              ? $t("howItWorks.products.seeMore")
              : $t("howItWorks.products.seeLess")
          }}
          <ChevronDownIcon
            class="size-6 transition-transform duration-200 ease-out"
            :class="isProductsListExpanded && 'rotate-180'"
          />
        </button>

        <a
          v-if="user?.current_mechanic === 'lactel'"
          type="button"
          class="text-secondary mx-auto flex cursor-pointer items-center gap-2 text-base font-medium underline lg:text-lg"
          href="https://www.enviedebienmanger.fr/sites/default/files/livrets-2025-06/LACTEL_Listing_produits_eligible.pdf"
          target="_blank"
          rel="noopener noreferrer"
          v-html="$t('howItWorks.products.seeMore')"
        />
      </section>

      <hr class="hidden h-px w-full border-0 bg-black/50 lg:block" aria-hidden="true" />

      <section id="claim" class="flex flex-col gap-5 lg:gap-6" aria-labelledby="claim-title">
        <h2
          id="claim-title"
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold lg:text-[32px]',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("footer.claim") }}
        </h2>

        <p
          class="text-secondary text-base font-medium lg:text-[18px]"
          v-html="$t('howItWorks.claim.subtitle')"
        />

        <h3
          :class="
            twMerge(
              'text-galbani text-xl font-semibold lg:text-xl',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
          v-html="$t('howItWorks.claim.item1.title')"
        />

        <div class="flex flex-col gap-2">
          <p
            class="text-secondary text-base font-medium lg:text-lg"
            v-html="$t('howItWorks.claim.item1.subtitle')"
          />

          <ol
            class="text-secondary list-inside list-decimal pl-5 text-base font-medium lg:text-lg"
            v-html="$t('howItWorks.claim.item1.list')"
          />
        </div>

        <h3
          :class="
            twMerge(
              'text-galbani text-xl font-semibold lg:text-xl',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
          v-html="$t('howItWorks.claim.item2.title')"
        />

        <div class="flex flex-col gap-2">
          <p
            class="text-secondary text-base font-medium lg:text-lg"
            v-html="$t('howItWorks.claim.item2.subtitle')"
          />

          <ol
            class="text-secondary list-inside list-decimal pl-5 text-base font-medium lg:text-lg"
            v-html="$t('howItWorks.claim.item2.list')"
          />
        </div>

        <h3
          :class="
            twMerge(
              'text-galbani text-xl font-semibold lg:text-xl',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
          v-html="$t('howItWorks.claim.item3.title')"
        />

        <div class="flex flex-col gap-2">
          <ol
            class="text-secondary list-inside list-decimal pl-5 text-base font-medium lg:text-lg"
            v-html="$t('howItWorks.claim.item3.list')"
          />
        </div>

        <p class="text-secondary text-base font-medium lg:text-[18px]">
          {{ $t("howItWorks.claim.information") }}
        </p>
      </section>

      <hr class="hidden h-px w-full border-0 bg-black/50 lg:block" aria-hidden="true" />

      <section id="help" class="flex flex-col gap-5 lg:gap-6" aria-labelledby="help-title">
        <h2
          id="help-title"
          :class="
            twMerge(
              'text-galbani text-2xl font-semibold lg:text-[32px]',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("howItWorks.help.title") }}
        </h2>

        <h3
          id="faq-title"
          :class="
            twMerge(
              'text-galbani text-xl font-semibold lg:hidden',
              user?.current_mechanic === 'lactel' && 'text-lactel-blue',
            )
          "
        >
          {{ $t("howItWorks.help.faq") }}
        </h3>

        <RadixAccordion.Root
          type="multiple"
          class="flex flex-col gap-5"
          aria-labelledby="faq-title"
        >
          <Accordion :label="$t('howItWorks.help.points.title')">
            <div v-html="$t('howItWorks.help.points.description')" />
          </Accordion>

          <Accordion :label="$t('howItWorks.help.rewards.title')">
            <div v-html="$t('howItWorks.help.rewards.description')" />
          </Accordion>

          <Accordion :label="$t('howItWorks.help.unsubscribe.title')">
            <div v-html="$t('howItWorks.help.unsubscribe.description')" />
          </Accordion>
        </RadixAccordion.Root>
      </section>

      <!-- Currently removed from both mechanics, keeping the code for reference (i18n translations) -->
      <!-- <section
        v-if="user?.current_mechanic === 'galbani'"
        class="flex flex-col gap-5 lg:gap-6"
        aria-labelledby="contact-title"
      >
        <h2
          id="contact-title"
          :class="twMerge('text-galbani text-2xl font-semibold lg:text-[32px]')"
        >
          {{ $t("howItWorks.contact.title") }}
        </h2>

        <p class="text-secondary text-base font-medium lg:text-xl">
          {{ $t("howItWorks.contact.subtitle") }}
        </p>

        <Button variant="secondary" class="mx-auto" @click="openMailto">
          {{ $t("howItWorks.contact.button") }}
          <EnvelopeIcon aria-hidden="true" />
        </Button>
      </section> -->
    </div>
  </main>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease-out;
}

.slide-enter-from {
  transform: translateY(12px);
  opacity: 0;
}

.slide-leave-to {
  transform: translateY(12px);
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  transform: translateY(0);
  opacity: 1;
}
</style>
